import { useEffect, useMemo, useState } from 'react';
import generate from './MandGen';
import { dimsForPixels, boundsAspect, THUMB_TARGET_PIXELS } from './renderSize';
import { heroBounds, heroDescent } from './heroSpecimen';
import yieldToPaint from './yieldToPaint';

// The landing page, computed. Returns the hero image, its exact bounds, and the
// descent frames — everything the page shows except the words.
//
// THE BUDGET. This is the one page where a slow first impression is fatal, and
// renderRegion is synchronous on the main thread, so the work is metered rather
// than simply asked for:
//
//   soft hero      ~14k px   arrives almost immediately, blurred, so the page is
//                            never a void; same trick as the gallery placeholder
//   sharp hero    ~300k px   the real image, still well under a generator render
//   4 descent frames ~14k px each
//   ---------------------------------------------------------------------------
//   total         ~370k px   roughly a third of ONE full-screen render
//
// The generator's own opening render is ~4.3M samples and is the entire reason the
// "Start zooming" gate exists. A landing page that spent that much would be the
// same mistake with better manners. Each step yields to the event loop, so the page
// stays tappable throughout and the frames arrive as they finish.
// A descent rung is displayed at the same size as a filmstrip thumbnail, so it
// costs exactly the same as one; the soft hero pass is deliberately the same budget.
const SOFT_PIXELS = THUMB_TARGET_PIXELS;
const SHARP_PIXELS = 300000;

const renderAt = (bounds, pixels) => generate({
    manual: true,
    ...dimsForPixels(boundsAspect(bounds), pixels),
    axisValues: {
        newMinR: bounds.realMin,
        newMaxR: bounds.realMax,
        newMinI: bounds.imaginaryMin,
        newMaxI: bounds.imaginaryMax
    }
});

const viewportAspect = () => window.innerWidth / window.innerHeight;

const useHero = () => {

    // Fitted to the viewport, so what the plate says is what the screen shows.
    const [aspect, setAspect] = useState(viewportAspect);
    const [hero, setHero] = useState({ dataUrl: null, sharp: false });
    const [descent, setDescent] = useState([]);

    // The plate reads these and encodes them into a share token on every render.
    const bounds = useMemo(() => heroBounds(aspect), [aspect]);

    // Only orientation changes, deliberately not every resize: a rotation is a large
    // aspect swing that would letterbox badly, while dragging a desktop window is a
    // small one that object-fit absorbs. Re-rendering on `resize` would hand a
    // multi-frame render job to every pixel of a window drag.
    useEffect(() => {
        const handle = () => setAspect(viewportAspect());
        window.addEventListener('orientationchange', handle);
        return () => window.removeEventListener('orientationchange', handle);
    }, []);

    useEffect(() => {
        let cancelled = false;

        setHero({ dataUrl: null, sharp: false });
        setDescent([]);

        (async () => {
            // Soft first: something real on screen within a frame or two, rather
            // than a page that withholds its only image until it is perfect.
            await yieldToPaint();
            if (cancelled) return;
            try {
                const soft = renderAt(bounds, SOFT_PIXELS);
                if (cancelled) return;
                setHero({ dataUrl: soft.dataUrl, sharp: false });
            } catch {
                /* no canvas: the page still reads, it just has no art */
            }

            await yieldToPaint();
            if (cancelled) return;
            try {
                const sharp = renderAt(bounds, SHARP_PIXELS);
                if (cancelled) return;
                setHero({ dataUrl: sharp.dataUrl, sharp: true });
            } catch {
                /* keep the soft one — better a blurred specimen than none */
            }

            // The ladder last. It is the supporting argument, and the hero is what
            // the visitor is actually looking at, so it does not get to compete for
            // the main thread until the hero has landed.
            for (const frame of heroDescent()) {
                await yieldToPaint();
                if (cancelled) return;
                try {
                    const rendered = renderAt(frame, SOFT_PIXELS);
                    if (cancelled) return;
                    setDescent(prev => [...prev, { bounds: frame, dataUrl: rendered.dataUrl }]);
                } catch {
                    /* an unrenderable rung leaves its frame pending, nothing worse */
                    return;
                }
            }
        })();

        return () => { cancelled = true; };
    }, [bounds]);

    return { bounds, hero, descent };
};

export default useHero;
